import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Navbar from '../components/Navbar';

function GroupDetails() {
  const { groupId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat');
  const [connections, setConnections] = useState([]);
  const [inviting, setInviting] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    fetchGroupDetails();
    fetchMembers();
  }, [groupId]);

  useEffect(() => {
    if (activeTab === 'invite') {
      fetchConnections();
    }
    if (activeTab === 'chat' && group?.is_member) {
      loadChatMessages();
      // Poll for new messages every 4 seconds
      pollInterval.current = setInterval(() => {
        loadChatMessages(true);
      }, 4000);
    }
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
        pollInterval.current = null;
      }
    };
  }, [activeTab, group]);

  useEffect(() => {
    if (activeTab === 'chat') {
      scrollToBottom();
    }
  }, [chatMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchGroupDetails = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroup(response.data.group);
    } catch (error) {
      console.error('Error fetching group:', error);
      setMessage('Failed to load group details');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/groups/${groupId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/connections', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const memberIds = members.map(m => m.user_id);
      const availableConnections = response.data.connections.filter(
        conn => !memberIds.includes(conn.connected_user_id)
      );
      setConnections(availableConnections);
    } catch (error) {
      console.error('Error fetching connections:', error);
    }
  };

  const loadChatMessages = async (silent = false) => {
    if (!silent) setChatLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/groups/${groupId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newMessages = response.data.messages || [];
      // Only update if messages changed (avoid unnecessary re-renders)
      if (!silent || JSON.stringify(newMessages) !== JSON.stringify(chatMessages)) {
        setChatMessages(newMessages);
      }
    } catch (error) {
      if (!silent) console.error('Error loading chat messages:', error);
    } finally {
      if (!silent) setChatLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newChatMessage.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`/api/groups/${groupId}/messages`,
        { text: newChatMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatMessages([...chatMessages, response.data.message]);
      setNewChatMessage('');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleInvite = async (userId) => {
    setInviting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`/api/groups/${groupId}/invite`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('✅ Invite sent successfully!');
      setConnections(connections.filter(c => c.connected_user_id !== userId));
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`/api/groups/${groupId}/leave`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/groups');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to leave group');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: { bg: '#007bff', text: 'Admin' },
      moderator: { bg: '#28a745', text: 'Moderator' },
      member: { bg: '#6c757d', text: 'Member' }
    };
    const style = styles[role] || styles.member;
    return (
      <span style={{ padding: '2px 8px', backgroundColor: style.bg, color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }}>
        {style.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="container"><div className="card">Loading...</div></div>
      </div>
    );
  }

  if (!group) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div className="card">
            <p>Group not found</p>
            <button onClick={() => navigate('/groups')} className="btn btn-primary">Back to Groups</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container">
        <div className="card">
          <button
            onClick={() => navigate('/groups')}
            style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '16px', marginBottom: '16px' }}
          >
            ← Back to Groups
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2>{group.name}</h2>
                {group.privacy === 'private' && (
                  <span style={{ padding: '4px 12px', backgroundColor: '#ffc107', color: '#000', borderRadius: '4px', fontSize: '14px', fontWeight: '500' }}>
                    🔒 Private
                  </span>
                )}
                {group.my_role && getRoleBadge(group.my_role)}
              </div>

              {group.target_identity && (
                <div style={{ marginTop: '12px' }}>
                  <span style={{ padding: '4px 12px', backgroundColor: '#e0f2fe', color: '#0369a1', borderRadius: '4px', fontSize: '14px', fontWeight: '500' }}>
                    {group.target_identity.icon} {group.target_identity.category_display}
                  </span>
                </div>
              )}

              <div style={{ marginTop: '12px', fontSize: '14px', color: '#999' }}>
                <span>👥 {group.member_count} member{group.member_count !== 1 ? 's' : ''}</span>
                {group.location_city && (
                  <span style={{ marginLeft: '16px' }}>📍 {group.location_city}, {group.location_state}</span>
                )}
                <span style={{ marginLeft: '16px' }}>Created {new Date(group.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {group.is_member && group.my_role !== 'admin' && (
              <button onClick={handleLeaveGroup} className="btn btn-secondary">Leave Group</button>
            )}
            {group.is_member && group.my_role === 'admin' && (
              <button
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to delete this group? This will remove all members and cannot be undone.')) return;
                  try {
                    const token = localStorage.getItem('accessToken');
                    await axios.delete(`/api/groups/${groupId}`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    navigate('/groups');
                  } catch (error) {
                    setMessage(error.response?.data?.error || 'Failed to delete group');
                  }
                }}
                className="btn btn-secondary"
                style={{ color: '#dc2626', borderColor: '#dc2626' }}
              >
                Delete Group
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
            <button onClick={() => setActiveTab('chat')} className={activeTab === 'chat' ? 'btn btn-primary' : 'btn btn-secondary'}>
              Chat
            </button>
            <button onClick={() => setActiveTab('members')} className={activeTab === 'members' ? 'btn btn-primary' : 'btn btn-secondary'}>
              Members ({members.length})
            </button>
            <button onClick={() => setActiveTab('about')} className={activeTab === 'about' ? 'btn btn-primary' : 'btn btn-secondary'}>
              About
            </button>
            {group.is_member && (
              <button onClick={() => setActiveTab('invite')} className={activeTab === 'invite' ? 'btn btn-primary' : 'btn btn-secondary'}>
                Invite
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className="card" style={{ backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da', color: message.includes('✅') ? '#155724' : '#721c24' }}>
            {message}
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {!group.is_member ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280' }}>
                <p>Join this group to participate in the chat.</p>
              </div>
            ) : chatLoading ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: '#9ca3af' }}>Loading messages...</div>
            ) : (
              <>
                <div style={{ height: '400px', overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {chatMessages.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: '100px' }}>
                      <p style={{ fontSize: '2rem' }}>👋</p>
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    chatMessages.map((msg) => (
                      <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.isMine ? 'flex-end' : 'flex-start' }}>
                        {!msg.isMine && (
                          <span style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px', marginLeft: '4px' }}>
                            {msg.senderName}
                          </span>
                        )}
                        <div style={{
                          maxWidth: '75%',
                          padding: '10px 14px',
                          borderRadius: msg.isMine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: msg.isMine ? '#7C3AED' : '#f3f4f6',
                          color: msg.isMine ? 'white' : '#1f2937',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          wordBreak: 'break-word'
                        }}>
                          {msg.text}
                        </div>
                        <span style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px', marginLeft: '4px', marginRight: '4px' }}>
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '8px', padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#fafafa' }}>
                  <input
                    type="text"
                    value={newChatMessage}
                    onChange={(e) => setNewChatMessage(e.target.value)}
                    placeholder="Type a message..."
                    maxLength={5000}
                    disabled={sending}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '20px', fontSize: '14px', outline: 'none' }}
                  />
                  <button
                    type="submit"
                    disabled={!newChatMessage.trim() || sending}
                    style={{
                      padding: '10px 18px', borderRadius: '20px', border: 'none',
                      background: newChatMessage.trim() ? '#7C3AED' : '#d1d5db',
                      color: 'white', fontWeight: '600', cursor: newChatMessage.trim() ? 'pointer' : 'not-allowed',
                      fontSize: '14px'
                    }}
                  >
                    {sending ? '...' : 'Send'}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="card">
            <h3>About This Group</h3>
            {group.description ? (
              <p style={{ marginTop: '12px', lineHeight: '1.6' }}>{group.description}</p>
            ) : (
              <p style={{ marginTop: '12px', color: '#999' }}>No description provided.</p>
            )}
            {group.creator_first_name && (
              <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #eee' }}>
                <h4>Created By</h4>
                <p style={{ marginTop: '8px' }}>{group.creator_first_name} {group.creator_last_name}</p>
              </div>
            )}
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div>
            {members.length === 0 ? (
              <div className="card"><p>No members yet.</p></div>
            ) : (
              members.map((member) => (
                <div key={member.user_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                    <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                      👤
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{member.first_name} {member.last_name}</strong>
                        {getRoleBadge(member.role)}
                      </div>
                      <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>📍 {member.current_city}, {member.current_state}</p>
                      <p style={{ marginTop: '4px', fontSize: '12px', color: '#999' }}>Joined {new Date(member.joined_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Invite Tab */}
        {activeTab === 'invite' && (
          <div>
            <div className="card">
              <h3>Invite Your Connections</h3>
              <p style={{ marginTop: '8px', color: '#666' }}>Invite people from your connections to join this group</p>
            </div>
            {connections.length === 0 ? (
              <div className="card">
                <p>No connections available to invite.</p>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                  All your connections are already members, or you don't have any connections yet.
                </p>
              </div>
            ) : (
              connections.map((conn) => (
                <div key={conn.connected_user_id} className="card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>
                      👤
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong>
                        {conn.disclosure_level >= 1
                          ? `${conn.first_name} ${conn.disclosure_level >= 2 ? conn.last_name : ''}`
                          : 'Anonymous User'}
                      </strong>
                      <p style={{ marginTop: '4px', fontSize: '14px', color: '#666' }}>📍 {conn.current_city}, {conn.current_state}</p>
                    </div>
                    <button onClick={() => handleInvite(conn.connected_user_id)} disabled={inviting} className="btn btn-primary">
                      {inviting ? 'Sending...' : 'Invite'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupDetails;
